import React, { useState, useMemo } from 'react';
import { Card, Row, Col, ButtonGroup, Button } from 'react-bootstrap';
import ReactECharts from 'echarts-for-react';
import PropTypes from 'prop-types';
import { useTheme } from '../../../../contexts/ThemeContext';

const ACTIVITY_COLORS = {
    running: '#FF6384',
    cycling: '#36A2EB',
    swimming: '#4BC0C0',
    hiking: '#FFCE56',
    walking: '#9966FF',
    default: '#C9CBCF'
};

const toTs = d => (d instanceof Date ? d : new Date(d)).getTime();

const fmtDate = ts =>
    new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

// ── Shared option builders ────────────────────────────────────────────────────

const colors = isDark => ({
    text: isDark ? '#e0e0e0' : '#333333',
    muted: isDark ? '#adb5bd' : '#6c757d',
    grid: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
});

const axisStyle = (tc, gc) => ({
    axisLabel: { color: tc, fontSize: 11 },
    axisLine: { lineStyle: { color: gc } },
    axisTick: { lineStyle: { color: gc } },
    splitLine: { lineStyle: { color: gc } },
});

const legend = (tc, isDark) => ({
    type: 'scroll',
    bottom: 0,
    textStyle: { color: tc, fontSize: 11 },
    pageTextStyle: { color: tc },
    inactiveColor: isDark ? '#555' : '#ccc',
    selectedMode: 'multiple',
});

const tooltipCss = isDark => {
    const bg = isDark ? 'rgba(28,28,28,0.96)' : 'rgba(255,255,255,0.96)';
    const border = isDark ? '#444' : '#ddd';
    const text = isDark ? '#e0e0e0' : '#333';
    return `background:${bg};border-color:${border};color:${text}`;
};

const lineSeriesBase = d => ({
    name: d.label,
    type: 'line',
    lineStyle: { color: d.borderColor, width: 2.5 },
    itemStyle: { color: d.borderColor },
    emphasis: { focus: 'series', blurScope: 'coordinateSystem', lineStyle: { width: 3.5 } },
    blur: { lineStyle: { opacity: 0.15 }, itemStyle: { opacity: 0.15 } },
    symbol: 'circle',
    symbolSize: 5,
});

// ── Component ─────────────────────────────────────────────────────────────────

const StatisticsView = ({
    competition,
    datasets,
    dailyDatasets,
    gapDatasets,
    totalActivities,
    totalPoints,
    winner,
    finalMargin,
    activityTypeBreakdown,
    winnerCalculationMethod,
}) => {
    const { theme } = useTheme();
    const isDarkMode = theme === 'dark';
    const [chartMode, setChartMode] = useState('cumulative');

    // winnerCalculationMethod may be passed as a top-level prop (from parent) or from competition
    const wcm = winnerCalculationMethod || competition?.winnerCalculationMethod;
    const { muted: mutedColor } = colors(isDarkMode);

    const sortedDatasets = useMemo(
        () => [...datasets].sort((a, b) => a.label.localeCompare(b.label)),
        [datasets]
    );
    const sortedDailyDatasets = useMemo(
        () => [...dailyDatasets].sort((a, b) => a.label.localeCompare(b.label)),
        [dailyDatasets]
    );
    const sortedNames = useMemo(
        () => Object.keys(activityTypeBreakdown || {}).sort((a, b) => a.localeCompare(b)),
        [activityTypeBreakdown]
    );

    // ── Points Progress ───────────────────────────────────────────────────────
    const mainOption = useMemo(() => {
        const { text: tc, muted: mc, grid: gc } = colors(isDarkMode);
        const ax = axisStyle(tc, gc);
        const css = tooltipCss(isDarkMode);
        const activeDs = chartMode === 'daily' ? sortedDailyDatasets : sortedDatasets;
        const smooth = chartMode === 'cumulative';

        return {
            backgroundColor: 'transparent',
            legend: { ...legend(tc, isDarkMode), data: activeDs.map(d => ({ name: d.label })) },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'cross', crossStyle: { color: mc } },
                confine: true,
                extraCssText: css,
                formatter: params => {
                    if (!params.length) return '';
                    const lines = params
                        .sort((a, b) => b.value[1] - a.value[1])
                        .map(p => `${p.marker}${p.seriesName}: <b>${Number(p.value[1]).toFixed(1)} pts</b>`)
                        .join('<br/>');
                    return `<span style="font-size:11px;color:${mc}">${fmtDate(params[0].value[0])}</span><br/>${lines}`;
                },
            },
            xAxis: { type: 'time', ...ax },
            yAxis: { type: 'value', name: smooth ? 'Cum. Points' : 'Points', nameTextStyle: { color: mc, fontSize: 11 }, ...ax },
            grid: { left: 8, right: 16, bottom: 40, top: 12, containLabel: true },
            series: activeDs.map(d => ({ ...lineSeriesBase(d), data: d.data.map(p => [toTs(p.x), p.y]), smooth })),
        };
    }, [isDarkMode, chartMode, sortedDatasets, sortedDailyDatasets]);

    // ── Gap Chart ─────────────────────────────────────────────────────────────
    const gapOption = useMemo(() => {
        const { text: tc, muted: mc, grid: gc } = colors(isDarkMode);
        const ax = axisStyle(tc, gc);
        const css = tooltipCss(isDarkMode);

        return {
            backgroundColor: 'transparent',
            legend: legend(tc, isDarkMode),
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'cross', crossStyle: { color: mc } },
                confine: true,
                extraCssText: css,
                formatter: params => {
                    if (!params.length) return '';
                    const lines = params
                        .sort((a, b) => a.value[1] - b.value[1])
                        .map(p => `${p.marker}${p.seriesName}: <b>${Number(p.value[1]).toFixed(1)} pts behind</b>`)
                        .join('<br/>');
                    return `<span style="font-size:11px;color:${mc}">${fmtDate(params[0].value[0])}</span><br/>${lines}`;
                },
            },
            xAxis: { type: 'time', ...ax },
            yAxis: { type: 'value', name: 'Points Behind', nameTextStyle: { color: mc, fontSize: 11 }, inverse: true, ...ax },
            grid: { left: 8, right: 16, bottom: 40, top: 12, containLabel: true },
            series: [...gapDatasets].sort((a, b) => a.label.localeCompare(b.label)).map(d => ({ ...lineSeriesBase(d), data: d.data.map(p => [toTs(p.x), p.y]), smooth: true })),
        };
    }, [isDarkMode, gapDatasets]);

    // ── Rank Timeline ─────────────────────────────────────────────────────────
    const rankOption = useMemo(() => {
        if (sortedDatasets.length < 2) return null;

        const { text: tc, muted: mc, grid: gc } = colors(isDarkMode);
        const ax = axisStyle(tc, gc);
        const css = tooltipCss(isDarkMode);

        // Collect all timestamps and carry-forward cumulative values
        const allTs = [...new Set(sortedDatasets.flatMap(d => d.data.map(p => toTs(p.x))))].sort((a, b) => a - b);
        if (!allTs.length) return null;

        const vals = sortedDatasets.map(d => {
            const map = Object.fromEntries(d.data.map(p => [toTs(p.x), p.y]));
            let last = 0;
            return allTs.map(ts => { if (map[ts] !== undefined) last = map[ts]; return last; });
        });

        const n = sortedDatasets.length;
        const series = sortedDatasets.map((d, ci) => ({
            ...lineSeriesBase(d),
            smooth: true,
            data: allTs.map((ts, di) => {
                const rank = vals.filter((_, oi) => vals[oi][di] > vals[ci][di]).length + 1;
                return [ts, rank];
            }),
        }));

        return {
            backgroundColor: 'transparent',
            legend: legend(tc, isDarkMode),
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'cross', crossStyle: { color: mc } },
                confine: true,
                extraCssText: css,
                formatter: params => {
                    if (!params.length) return '';
                    const lines = params
                        .sort((a, b) => a.value[1] - b.value[1])
                        .map(p => `${p.marker}${p.seriesName}: <b>#${p.value[1]}</b>`)
                        .join('<br/>');
                    return `<span style="font-size:11px;color:${mc}">${fmtDate(params[0].value[0])}</span><br/>${lines}`;
                },
            },
            xAxis: { type: 'time', ...ax },
            yAxis: {
                type: 'value',
                inverse: true,
                min: 1,
                max: n,
                minInterval: 1,
                maxInterval: 1,
                axisLabel: { color: tc, fontSize: 11, formatter: v => `#${v}` },
                axisLine: { lineStyle: { color: gc } },
                axisTick: { lineStyle: { color: gc } },
                splitLine: { lineStyle: { color: gc } },
            },
            grid: { left: 8, right: 16, bottom: 40, top: 12, containLabel: true },
            series,
        };
    }, [isDarkMode, sortedDatasets]);

    // ── 7-day Momentum ────────────────────────────────────────────────────────
    const momentumOption = useMemo(() => {
        if (!sortedDailyDatasets.some(d => d.data.length >= 3)) return null;

        const { text: tc, muted: mc, grid: gc } = colors(isDarkMode);
        const ax = axisStyle(tc, gc);
        const css = tooltipCss(isDarkMode);
        const W = 7;

        const series = sortedDailyDatasets.filter(d => d.data.length > 0).map(d => ({
            ...lineSeriesBase(d),
            smooth: true,
            symbol: 'none',
            areaStyle: { color: d.borderColor, opacity: 0.08 },
            data: d.data.map((p, i) => {
                const slice = d.data.slice(Math.max(0, i - W + 1), i + 1);
                const avg = slice.reduce((s, wp) => s + wp.y, 0) / slice.length;
                return [toTs(p.x), parseFloat(avg.toFixed(2))];
            }),
        }));

        return {
            backgroundColor: 'transparent',
            legend: legend(tc, isDarkMode),
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'cross', crossStyle: { color: mc } },
                confine: true,
                extraCssText: css,
                formatter: params => {
                    if (!params.length) return '';
                    const lines = params
                        .sort((a, b) => b.value[1] - a.value[1])
                        .map(p => `${p.marker}${p.seriesName}: <b>${Number(p.value[1]).toFixed(1)} pts/day</b>`)
                        .join('<br/>');
                    return `<span style="font-size:11px;color:${mc}">${fmtDate(params[0].value[0])}</span><br/>${lines}`;
                },
            },
            xAxis: { type: 'time', ...ax },
            yAxis: { type: 'value', name: 'Avg pts / day', nameTextStyle: { color: mc, fontSize: 11 }, ...ax },
            grid: { left: 8, right: 16, bottom: 40, top: 12, containLabel: true },
            series,
        };
    }, [isDarkMode, sortedDailyDatasets]);

    // ── Activity Count Breakdown ──────────────────────────────────────────────
    const activityCountOption = useMemo(() => {
        const types = [...new Set(sortedNames.flatMap(n => Object.keys(activityTypeBreakdown[n])))];
        if (!sortedNames.length || !types.length) return null;

        const { text: tc, muted: mc, grid: gc } = colors(isDarkMode);
        const ax = axisStyle(tc, gc);
        const css = tooltipCss(isDarkMode);

        return {
            backgroundColor: 'transparent',
            legend: legend(tc, isDarkMode),
            tooltip: {
                trigger: 'axis', axisPointer: { type: 'shadow' }, confine: true, extraCssText: css,
                formatter: params => {
                    const name = params[0]?.axisValueLabel || '';
                    const lines = params.filter(p => p.value > 0)
                        .map(p => `${p.marker}${p.seriesName}: <b>${p.value}</b>`).join('<br/>');
                    return `<b>${name}</b><br/>${lines || 'No activities'}`;
                },
            },
            xAxis: { type: 'category', data: sortedNames, ...ax, axisLabel: { ...ax.axisLabel, rotate: 30, interval: 0 } },
            yAxis: { type: 'value', name: 'Activities', nameTextStyle: { color: mc, fontSize: 11 }, minInterval: 1, ...ax },
            grid: { left: 8, right: 8, bottom: 60, top: 12, containLabel: true },
            series: types.map(type => ({
                name: type.charAt(0).toUpperCase() + type.slice(1),
                type: 'bar', stack: 'total',
                data: sortedNames.map(n => activityTypeBreakdown[n][type]?.count || 0),
                itemStyle: { color: ACTIVITY_COLORS[type.toLowerCase()] || ACTIVITY_COLORS.default },
                emphasis: { focus: 'series' },
                label: { show: true, position: 'inside', formatter: p => (p.value > 0 ? p.value : ''), fontSize: 11, color: '#fff' },
            })),
        };
    }, [isDarkMode, activityTypeBreakdown, sortedNames]);

    // ── Points by Activity Type ───────────────────────────────────────────────
    const activityPointsOption = useMemo(() => {
        const types = [...new Set(sortedNames.flatMap(n => Object.keys(activityTypeBreakdown[n])))];
        if (!sortedNames.length || !types.length) return null;

        const { text: tc, muted: mc, grid: gc } = colors(isDarkMode);
        const ax = axisStyle(tc, gc);
        const css = tooltipCss(isDarkMode);

        return {
            backgroundColor: 'transparent',
            legend: legend(tc, isDarkMode),
            tooltip: {
                trigger: 'axis', axisPointer: { type: 'shadow' }, confine: true, extraCssText: css,
                formatter: params => {
                    const name = params[0]?.axisValueLabel || '';
                    const lines = params.filter(p => p.value > 0)
                        .map(p => `${p.marker}${p.seriesName}: <b>${Number(p.value).toFixed(1)} pts</b>`).join('<br/>');
                    return `<b>${name}</b><br/>${lines || 'No activities'}`;
                },
            },
            xAxis: { type: 'category', data: sortedNames, ...ax, axisLabel: { ...ax.axisLabel, rotate: 30, interval: 0 } },
            yAxis: { type: 'value', name: 'Points', nameTextStyle: { color: mc, fontSize: 11 }, ...ax },
            grid: { left: 8, right: 8, bottom: 60, top: 12, containLabel: true },
            series: types.map(type => ({
                name: type.charAt(0).toUpperCase() + type.slice(1),
                type: 'bar', stack: 'total',
                data: sortedNames.map(n => parseFloat((activityTypeBreakdown[n][type]?.points || 0).toFixed(1))),
                itemStyle: { color: ACTIVITY_COLORS[type.toLowerCase()] || ACTIVITY_COLORS.default },
                emphasis: { focus: 'series' },
                label: { show: true, position: 'inside', formatter: p => (p.value >= 1 ? p.value.toFixed(0) : ''), fontSize: 11, color: '#fff' },
            })),
        };
    }, [isDarkMode, activityTypeBreakdown, sortedNames]);

    // ── Radar Chart ───────────────────────────────────────────────────────────
    const radarOption = useMemo(() => {
        const types = [...new Set(sortedNames.flatMap(n => Object.keys(activityTypeBreakdown[n])))];
        if (!sortedNames.length || types.length < 2) return null;

        const { text: tc, grid: gc } = colors(isDarkMode);
        const css = tooltipCss(isDarkMode);
        const colorMap = Object.fromEntries(sortedDatasets.map(d => [d.label, d.borderColor]));
        // eslint-disable-next-line no-unused-vars
        const maxPerType = types.map(type =>
            Math.max(...sortedNames.map(n => activityTypeBreakdown[n][type]?.points || 0), 1)
        );

        return {
            backgroundColor: 'transparent',
            legend: { ...legend(tc, isDarkMode), top: 'auto', bottom: 8 },
            tooltip: {
                confine: true, extraCssText: css,
                formatter: params => {
                    const { name, value } = params.data;
                    const c = colorMap[name] || '#999';
                    const lines = types.map((type, i) =>
                        `<span style="display:inline-block;margin-right:4px;width:8px;height:8px;border-radius:50%;background:${c}"></span>${type.charAt(0).toUpperCase() + type.slice(1)}: <b>${Number(value[i]).toFixed(1)} pts</b>`
                    ).join('<br/>');
                    return `<b>${name}</b><br/>${lines}`;
                },
            },
            radar: {
                center: ['50%', '46%'],
                radius: '60%',
                indicator: types.map((type, i) => ({
                    name: type.charAt(0).toUpperCase() + type.slice(1),
                    max: maxPerType[i],
                })),
                axisName: { color: tc, fontSize: 12 },
                splitLine: { lineStyle: { color: gc } },
                axisLine: { lineStyle: { color: gc } },
                splitArea: {
                    show: true,
                    areaStyle: {
                        color: isDarkMode
                            ? ['rgba(255,255,255,0.02)', 'rgba(255,255,255,0.05)']
                            : ['rgba(0,0,0,0.01)', 'rgba(0,0,0,0.03)'],
                    },
                },
            },
            series: [{
                type: 'radar',
                data: sortedNames.map(name => {
                    const c = colorMap[name] || '#999';
                    return {
                        name,
                        value: types.map(type => activityTypeBreakdown[name][type]?.points || 0),
                        lineStyle: { color: c, width: 2 },
                        areaStyle: { color: c, opacity: 0.12 },
                        itemStyle: { color: c },
                        symbol: 'circle',
                        symbolSize: 6,
                    };
                }),
            }],
        };
    }, [isDarkMode, activityTypeBreakdown, sortedNames, sortedDatasets]);

    // ── Consistency Heatmap ───────────────────────────────────────────────────
    const heatmapOption = useMemo(() => {
        if (!sortedDailyDatasets.some(d => d.data.length > 0)) return null;

        const { text: tc, grid: gc } = colors(isDarkMode);
        const css = tooltipCss(isDarkMode);
        const sorted = [...sortedDailyDatasets].reverse(); // ECharts y-axis renders bottom-up, so reverse to show A-Z top-to-bottom
        const names = sorted.map(d => d.label);

        const allTs = [...new Set(sorted.flatMap(d => d.data.map(p => toTs(p.x))))].sort((a, b) => a - b);
        if (!allTs.length) return null;

        const labelStep = Math.max(1, Math.ceil(allTs.length / 12));
        const xLabels = allTs.map((ts, i) => (i % labelStep === 0 ? fmtDate(ts) : ''));

        let maxVal = 0;
        const data = sorted.flatMap((d, yi) => {
            const map = Object.fromEntries(d.data.map(p => [toTs(p.x), p.y]));
            return allTs.map((ts, xi) => {
                const v = parseFloat((map[ts] || 0).toFixed(1));
                if (v > maxVal) maxVal = v;
                return [xi, yi, v];
            });
        });

        return {
            backgroundColor: 'transparent',
            tooltip: {
                confine: true, extraCssText: css,
                formatter: p => {
                    const [xi, yi, val] = p.data;
                    const date = fmtDate(allTs[xi]);
                    return val > 0
                        ? `<b>${names[yi]}</b> · ${date}<br/><b>${val.toFixed(1)} pts</b>`
                        : `<b>${names[yi]}</b> · ${date}<br/>No activity`;
                },
            },
            grid: { left: 8, right: 20, bottom: 36, top: 12, containLabel: true },
            xAxis: {
                type: 'category', data: xLabels,
                axisLabel: { color: tc, fontSize: 10, rotate: 45, interval: 0 },
                axisLine: { lineStyle: { color: gc } },
                axisTick: { show: false },
                splitLine: { show: false },
            },
            yAxis: {
                type: 'category', data: names,
                axisLabel: { color: tc, fontSize: 11 },
                axisLine: { lineStyle: { color: gc } },
                axisTick: { show: false },
                splitLine: { show: false },
            },
            visualMap: {
                min: 0, max: Math.max(maxVal, 1),
                show: false,
                inRange: {
                    color: isDarkMode
                        ? ['#1a2e1a', '#52c41a', '#135200']
                        : ['#f0fff0', '#73d13d', '#135200'],
                },
            },
            series: [{
                type: 'heatmap', data,
                itemStyle: { borderWidth: 2, borderColor: isDarkMode ? '#111' : '#fff' },
                emphasis: { itemStyle: { shadowBlur: 8, shadowColor: 'rgba(0,0,0,0.4)' } },
            }],
        };
    }, [isDarkMode, sortedDailyDatasets]);

    // ── Render ────────────────────────────────────────────────────────────────

    const activeDatasets = chartMode === 'daily' ? sortedDailyDatasets : sortedDatasets;
    const hasMainData = activeDatasets.some(d => d.data.length > 0);
    const hasGapData = gapDatasets?.length > 0;
    const hasActivity = sortedNames.length > 0;
    const isActive = competition.daysRemaining > 0;
    const heatmapHeight = Math.max(120, sortedDailyDatasets.length * 30 + 60);

    return (
        <div>
            {/* ── Overview ── */}
            <Card className="mb-4">
                <Card.Header><h4 className="mb-0">Competition Overview</h4></Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={3} sm={6} className="mb-3 mb-md-0">
                            <div className="text-center">
                                <h6 className="mb-1" style={{ color: mutedColor }}>Total Activities</h6>
                                <p className="h2 mb-0">{totalActivities}</p>
                            </div>
                        </Col>
                        <Col md={3} sm={6} className="mb-3 mb-md-0">
                            <div className="text-center">
                                <h6 className="mb-1" style={{ color: mutedColor }}>Total Points</h6>
                                <p className="h2 mb-0">{totalPoints.toFixed(1)}</p>
                            </div>
                        </Col>
                        <Col md={3} sm={6} className="mb-3 mb-md-0">
                            <div className="text-center">
                                <h6 className="mb-1" style={{ color: mutedColor }}>Competitors</h6>
                                <p className="h2 mb-0">{competition.competitors?.length || 0}</p>
                            </div>
                        </Col>
                        <Col md={3} sm={6}>
                            <div className="text-center">
                                 {isActive ? (
                                    <>
                                        <h6 className="mb-1" style={{ color: mutedColor }}>Days Remaining</h6>
                                        <p className="h2 mb-0">{competition.daysRemaining}</p>
                                    </>
                                ) : winner ? (
                                    <>
                                        <h6 className="mb-1" style={{ color: mutedColor }}>Winner</h6>
                                        <p className="h2 mb-0">{winner}</p>
                                        {finalMargin !== null && (
                                            <small style={{ color: mutedColor }}>by {finalMargin} pts</small>
                                        )}
                                        {wcm === 'POINT_AVERAGE' && (
                                            <small style={{ color: mutedColor }}>(Winner determined by average points per activity)</small>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <h6 className="mb-1" style={{ color: mutedColor }}>Status</h6>
                                        <p className="h4 mb-0">Concluded</p>
                                    </>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* ── Points Progress ── */}
            <Card className="mb-4">
                <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <h4 className="mb-0">Points Progress</h4>
                    <ButtonGroup size="sm">
                        <Button variant={chartMode === 'cumulative' ? 'primary' : 'outline-secondary'} onClick={() => setChartMode('cumulative')}>Cumulative</Button>
                        <Button variant={chartMode === 'daily' ? 'primary' : 'outline-secondary'} onClick={() => setChartMode('daily')}>Daily</Button>
                    </ButtonGroup>
                </Card.Header>
                <Card.Body>
                    {hasMainData
                        ? <ReactECharts option={mainOption} style={{ height: '380px' }} />
                        : <div className="text-center py-4" style={{ color: mutedColor }}>No activity data available yet.</div>
                    }
                </Card.Body>
            </Card>

            {/* ── Rank Timeline ── */}
            {rankOption && (
                <Card className="mb-4">
                    <Card.Header>
                        <h4 className="mb-0">Rank Timeline</h4>
                        <small style={{ color: mutedColor }}>Who was in the lead and when</small>
                    </Card.Header>
                    <Card.Body>
                        <ReactECharts option={rankOption} style={{ height: '300px' }} />
                    </Card.Body>
                </Card>
            )}

            {/* ── Points Gap ── */}
            {hasGapData && (
                <Card className="mb-4">
                    <Card.Header><h4 className="mb-0">Points Gap from Leader</h4></Card.Header>
                    <Card.Body>
                        <ReactECharts option={gapOption} style={{ height: '300px' }} />
                    </Card.Body>
                </Card>
            )}

            {/* ── 7-day Momentum ── */}
            {momentumOption && (
                <Card className="mb-4">
                    <Card.Header>
                        <h4 className="mb-0">7-Day Momentum</h4>
                        <small style={{ color: mutedColor }}>Rolling average daily points — who is on a hot streak</small>
                    </Card.Header>
                    <Card.Body>
                        <ReactECharts option={momentumOption} style={{ height: '300px' }} />
                    </Card.Body>
                </Card>
            )}

            {/* ── Activity Breakdown (count + points side by side) ── */}
            {hasActivity && (
                <Row className="mb-4">
                    <Col md={6} className="mb-4 mb-md-0">
                        <Card className="h-100">
                            <Card.Header><h4 className="mb-0">Activity Count by Type</h4></Card.Header>
                            <Card.Body>
                                {activityCountOption && <ReactECharts option={activityCountOption} style={{ height: '260px' }} />}
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={6}>
                        <Card className="h-100">
                            <Card.Header><h4 className="mb-0">Points by Activity Type</h4></Card.Header>
                            <Card.Body>
                                {activityPointsOption && <ReactECharts option={activityPointsOption} style={{ height: '260px' }} />}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* ── Radar Chart ── */}
            {radarOption && (
                <Card className="mb-4">
                    <Card.Header>
                        <h4 className="mb-0">Activity Profile</h4>
                        <small style={{ color: mutedColor }}>Points per activity type — specialist vs all-rounder</small>
                    </Card.Header>
                    <Card.Body>
                        <ReactECharts option={radarOption} style={{ height: '360px' }} />
                    </Card.Body>
                </Card>
            )}

            {/* ── Consistency Heatmap ── */}
            {heatmapOption && (
                <Card className="mb-4">
                    <Card.Header>
                        <h4 className="mb-0">Activity Consistency</h4>
                        <small style={{ color: mutedColor }}>Daily points intensity</small>
                    </Card.Header>
                    <Card.Body>
                        <ReactECharts option={heatmapOption} style={{ height: `${heatmapHeight}px` }} />
                    </Card.Body>
                </Card>
            )}
        </div>
    );
};

  StatisticsView.propTypes = {
    competition: PropTypes.shape({
        competitors: PropTypes.array,
        daysRemaining: PropTypes.number.isRequired
    }).isRequired,
    datasets: PropTypes.array.isRequired,
    dailyDatasets: PropTypes.array.isRequired,
    gapDatasets: PropTypes.array.isRequired,
    totalActivities: PropTypes.number.isRequired,
    totalPoints: PropTypes.number.isRequired,
    winner: PropTypes.string,
    finalMargin: PropTypes.number,
    activityTypeBreakdown: PropTypes.object.isRequired,
    winnerCalculationMethod: PropTypes.string,
};

export default StatisticsView;
